import { BRAND } from "../api";
import { brandLinks } from "../constants/brandLinks";
import { defaultCountryCodeByBrand } from "./phoneUtils";
import { BrandingLinkItem } from "../store/common/configuration";

/**
 * Get image URL from branding API response by type key.
 * Images come as array of single-key objects: [{ "logo": "url" }, { "favicon": "url" }]
 */
export const getBrandingImageUrl = (
  images: any,
  type: string,
): string | null => {
  if (!images?.length) return null;
  const item = images.find((img:any) => img[type] !== undefined);
  return item?.[type] || null;
};

/**
 * Get logo URL: API image → static fallback
 */
export const getBrandingLogo = (
  images: any,
): string => {
  return getBrandingImageUrl(images, 'logo') || `/assets/images/logo/${BRAND}/logo.svg`;
};

/**
 * Get support email: API → brandLinks fallback
 */
export const getBrandingSupportEmail = (
  brandingData: any,
): string => {
  if (brandingData?.supportEmail) return brandingData.supportEmail;
  return brandLinks[BRAND as string]?.email || '';
};

/**
 * Get social links: API → brandLinks fallback
 */
export const getBrandingLinks = (
  brandingData: any,
): BrandingLinkItem[] => {
  if (brandingData?.links?.length) return brandingData.links;
  return [];
};

/**
 * Get phone mask country code: API → defaultCountryCodeByBrand fallback
 */
export const getBrandingPhoneMaskCountry = (
  brandingData: any,
): string => {
  if (brandingData?.phoneMaskCountry) return brandingData.phoneMaskCountry;
  return defaultCountryCodeByBrand(BRAND as string).code;
};

/**
 * Get favicon URL by size key: API image → static fallback
 */
export const getBrandingFavicon = (
  images: any,
  sizeKey: string,
): string => {
  const url = getBrandingImageUrl(images, sizeKey);
  if (url) return url;
  // static fallback path
  const ext = sizeKey === 'favicon' ? 'ico' : 'png';
  return `/assets/favicons/${BRAND}/${sizeKey}.${ext}`;
};

/**
 * Get loader image URL: API → static logo fallback
 */
export const getBrandingLoader = (
  images: any,
  mode: 'light' | 'dark' = 'light',
): string => {
  const key = mode === 'dark' ? 'loader_dark' : 'loader_light';
  const url = getBrandingImageUrl(images, key);
  if (url) return url;
  // fallback to static logo
  return mode === 'dark'
    ? `/assets/images/logo/${BRAND}/logo-solid.svg`
    : `/assets/images/logo/${BRAND}/logo-large.svg`;
};

/**
 * Get background image URL: API → null (no fallback, component handles its own static import)
 */
export const getBrandingBackground = (
  images: any,
): string | null => {
  return getBrandingImageUrl(images, 'background');
};
